import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import {
  FileText, Download, Shield, ShieldOff, ChevronLeft, ChevronRight,
  Users, FileCheck, AlertTriangle, Clock, Filter, RefreshCw, Ban, CheckCircle, Brain
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { AdminAiAnalyses } from "../components/AdminAiAnalyses";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminValidationsPage = ({ onBack }) => {
  const { user } = useAuth();
  const [validations, setValidations] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [blockedIps, setBlockedIps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("validations"); // "validations" | "visitors" | "blocked"
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ type: "", anonymousOnly: false });

  const fetchValidations = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API}/admin/validations?page=${page}&limit=50`;
      if (filter.type) url += `&validation_type=${filter.type}`;
      if (filter.anonymousOnly) url += `&anonymous_only=true`;
      
      const res = await axios.get(url, { withCredentials: true });
      setValidations(res.data.validations || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch validations:", err);
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  const fetchVisitors = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/anonymous-visitors`, { withCredentials: true });
      setVisitors(res.data.visitors || []);
    } catch (err) {
      console.error("Failed to fetch visitors:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBlockedIps = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/blocked-ips`, { withCredentials: true });
      setBlockedIps(res.data.blocked_ips || []);
    } catch (err) {
      console.error("Failed to fetch blocked IPs:", err);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "validations") {
      fetchValidations();
    } else if (activeTab === "visitors") {
      fetchVisitors();
    } else if (activeTab === "blocked") {
      fetchBlockedIps();
    }
  }, [activeTab, fetchValidations, fetchVisitors, fetchBlockedIps]);

  const handleDownload = async (validationId, filename) => {
    try {
      const res = await axios.get(`${API}/admin/validation/${validationId}/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'validation.xml');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to download:", err);
      alert("Erreur lors du téléchargement");
    }
  };

  const handleBlockIp = async (ip, block) => {
    try {
      await axios.post(`${API}/admin/block-ip?ip=${encodeURIComponent(ip)}&block=${block}`, {}, { withCredentials: true });
      // Refresh data
      if (activeTab === "visitors") fetchVisitors();
      if (activeTab === "blocked") fetchBlockedIps();
    } catch (err) {
      console.error("Failed to block/unblock IP:", err);
      alert("Erreur lors du blocage/déblocage");
    }
  };

  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Shield className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Accès refusé</h2>
          <p className="text-sm text-slate-500 mt-2">Cette page est réservée aux administrateurs.</p>
          <Button onClick={onBack} className="mt-4">Retour</Button>
        </div>
      </div>
    );
  }

  const getTypeColor = (type) => {
    switch (type) {
      case "ubl": return "bg-blue-100 text-blue-700";
      case "cii": return "bg-purple-100 text-purple-700";
      case "cdar": return "bg-emerald-100 text-emerald-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={onBack} className="gap-2">
                <ChevronLeft className="h-4 w-4" />
                Admin
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <h1 className="text-xl font-bold text-slate-800">Historique des validations</h1>
            </div>
            <Button variant="outline" onClick={() => {
              if (activeTab === "validations") fetchValidations();
              else if (activeTab === "visitors") fetchVisitors();
              else fetchBlockedIps();
            }} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Actualiser
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === "validations" ? "default" : "outline"}
            onClick={() => setActiveTab("validations")}
            className="gap-2"
            data-testid="tab-validations"
          >
            <FileCheck className="h-4 w-4" />
            Validations ({total})
          </Button>
          <Button
            variant={activeTab === "visitors" ? "default" : "outline"}
            onClick={() => setActiveTab("visitors")}
            className="gap-2"
            data-testid="tab-anonymous"
          >
            <Users className="h-4 w-4" />
            Visiteurs anonymes
          </Button>
          <Button
            variant={activeTab === "blocked" ? "default" : "outline"}
            onClick={() => setActiveTab("blocked")}
            className="gap-2"
            data-testid="tab-blocked"
          >
            <Ban className="h-4 w-4" />
            IPs bloquées ({blockedIps.length})
          </Button>
          <Button
            variant={activeTab === "ai" ? "default" : "outline"}
            onClick={() => setActiveTab("ai")}
            className="gap-2"
            data-testid="tab-ai-analyses"
          >
            <Brain className="h-4 w-4" />
            Analyses IA
          </Button>
        </div>

        {/* AI Analyses Tab */}
        {activeTab === "ai" && <AdminAiAnalyses />}

        {/* Validations Tab */}
        {activeTab === "validations" && (
          <>
            {/* Filters */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 flex items-center gap-4">
              <Filter className="h-4 w-4 text-slate-400" />
              <select
                value={filter.type}
                onChange={(e) => { setFilter(f => ({ ...f, type: e.target.value })); setPage(1); }}
                className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
              >
                <option value="">Tous les types</option>
                <option value="ubl">UBL</option>
                <option value="cii">Factur-X / CII</option>
                <option value="cdar">CDAR</option>
              </select>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={filter.anonymousOnly}
                  onChange={(e) => { setFilter(f => ({ ...f, anonymousOnly: e.target.checked })); setPage(1); }}
                  className="rounded"
                />
                Anonymes uniquement
              </label>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {loading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mx-auto" />
                </div>
              ) : validations.length === 0 ? (
                <div className="p-8 text-center text-slate-500">Aucune validation trouvée</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Fichier</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Type</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Statut</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">IP</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">User</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Date</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {validations.map((v, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-slate-400" />
                              <span className="font-medium text-slate-800 truncate max-w-[200px]">{v.filename}</span>
                            </div>
                            <span className="text-xs text-slate-400">{(v.file_size / 1024).toFixed(1)} KB</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getTypeColor(v.validation_type)}`}>
                              {v.validation_type?.toUpperCase() || "UBL"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {v.valid ? (
                              <span className="flex items-center gap-1 text-emerald-600">
                                <CheckCircle className="h-4 w-4" /> Valide
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-red-600">
                                <AlertTriangle className="h-4 w-4" /> Invalide
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-slate-500">
                            {v.client_ip || "-"}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {v.user_email ? (
                              <span className="text-blue-600 text-xs">{v.user_email}</span>
                            ) : v.user_id ? (
                              <span className="text-blue-600 text-xs">{v.user_id.slice(0, 12)}...</span>
                            ) : (
                              <span className="text-slate-400 italic">Anonyme</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {v.created_at || "-"}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownload(v.file_hash?.slice(0, 16) || v.validation_id, v.filename)}
                              className="gap-1"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="border-t border-slate-200 px-4 py-3 flex items-center justify-between">
                  <span className="text-sm text-slate-500">
                    Page {page} sur {totalPages} ({total} résultats)
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Visitors Tab */}
        {activeTab === "visitors" && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mx-auto" />
              </div>
            ) : visitors.length === 0 ? (
              <div className="p-8 text-center text-slate-500">Aucun visiteur anonyme trouvé</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">IP</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Fingerprint</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Validations</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Types</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Dernière activité</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Fichiers récents</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {visitors.map((v, i) => (
                      <tr key={i} className={`hover:bg-slate-50 ${v.blocked ? 'bg-red-50' : ''}`}>
                        <td className="px-4 py-3 font-mono text-sm">
                          {v.ip || "-"}
                          {v.blocked && <span className="ml-2 text-red-600 text-xs">(bloqué)</span>}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-400 truncate max-w-[150px]">
                          {v.fingerprint || "-"}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-slate-800">{v.validation_count}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            {v.validation_types?.map((t, j) => (
                              <span key={j} className={`px-1.5 py-0.5 rounded text-xs ${getTypeColor(t)}`}>
                                {t?.toUpperCase()}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs">
                          {v.last_seen ? new Date(v.last_seen).toLocaleString('fr-FR') : "-"}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500 max-w-[200px]">
                          {v.recent_files?.slice(0, 3).join(", ") || "-"}
                        </td>
                        <td className="px-4 py-3">
                          {v.blocked ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleBlockIp(v.ip, false)}
                              className="gap-1 text-emerald-600 hover:text-emerald-700"
                            >
                              <ShieldOff className="h-4 w-4" />
                              Débloquer
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleBlockIp(v.ip, true)}
                              className="gap-1 text-red-600 hover:text-red-700"
                            >
                              <Shield className="h-4 w-4" />
                              Bloquer
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Blocked IPs Tab */}
        {activeTab === "blocked" && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {blockedIps.length === 0 ? (
              <div className="p-8 text-center text-slate-500">Aucune IP bloquée</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {blockedIps.map((b, i) => (
                  <div key={i} className="px-6 py-4 flex items-center justify-between">
                    <div>
                      <span className="font-mono text-slate-800">{b.ip}</span>
                      <span className="text-xs text-slate-400 ml-3">
                        Bloqué le {b.updated_at ? new Date(b.updated_at).toLocaleString('fr-FR') : "-"}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleBlockIp(b.ip, false)}
                      className="gap-1"
                    >
                      <ShieldOff className="h-4 w-4" />
                      Débloquer
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminValidationsPage;
